import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useOutletContext, useLocation } from 'react-router-dom';
import { usersAPI, peoplePlatformAPI, authAPI } from '@/services';
import { mapPeopleToMemberRow, digitsOnly } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { isOrgAdminRole, formatIamRoleLabel } from '../utils/iamRoles';
import PINInput from '../components/PINInput';
import TableListCheckbox from '../components/TableListCheckbox';
import ModalShell from '../components/ModalShell';
import Pagination from '../components/Pagination';
import { staffMembersBasePath, staffUsersBasePath, staffUsersListPath } from '../utils/portalAdminNav';

function TabNavIcon({ tabId }) {
  const cls = 'w-4 h-4 md:w-[18px] md:h-[18px] flex-shrink-0 opacity-90';
  switch (tabId) {
    case 'info':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case 'member':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 10-5.656-5.656l-1.1 1.1" />
        </svg>
      );
    case 'validation':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'sessions':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    case 'logs':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      );
    default:
      return null;
  }
}

/** Ligne label / valeur pour les fiches (lisibilité). */
function DetailRow({ label, children, hint }) {
  return (
    <div className="rounded-xl border border-slate-200/90 bg-slate-50/40 px-3 py-2.5 md:px-4 md:py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm text-[#0f3350] font-medium break-words">{children}</div>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

const UserDetails = () => {
  const outletCtx = useOutletContext() || {};
  const profileShellCompte = outletCtx.profileShell === 'compte';
  const { id: routeUserId } = useParams();
  const id = outletCtx.profileUserId ?? routeUserId;
  const navigate = useNavigate();
  const location = useLocation();
  const usersListPath = staffUsersListPath(location.pathname);
  const usersBase = staffUsersBasePath(location.pathname);
  const membersBase = staffMembersBasePath(location.pathname);
  const { error, success } = useToast();
  const { user: currentUser } = useAuth();
  const { role, hasPermission } = usePermissions();
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [auditLogs, setAuditLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [logsFilters, setLogsFilters] = useState({
    action: '',
    resource_type: '',
    startDate: '',
    endDate: ''
  });
  const [sessionsFilters, setSessionsFilters] = useState({
    startDate: '',
    endDate: '',
    isActiveOnly: false
  });
  const [validationInfo, setValidationInfo] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [sessionsPageSize, setSessionsPageSize] = useState(20);
  const [logsPageSize, setLogsPageSize] = useState(20);
  
  // States pour la modification du profil utilisateur
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userEditData, setUserEditData] = useState({
    username: '',
    email: ''
  });
  
  // States pour la modification du mot de passe et PIN
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPINModal, setShowPINModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    /** Réinitialisation admin : imposer le changement au prochain login */
    forceNextLogin: true,
  });
  const [pinData, setPinData] = useState({
    oldPin: '',
    newPin: '',
    confirmPin: ''
  });
  const [saving, setSaving] = useState(false);
  const linkedPersonId = user?.person_id || user?.member_id || '';
  
  // L'utilisateur peut modifier son propre compte, ou si c'est un admin
  const isOwnAccount = currentUser?.id === id;
  const canEdit = isOwnAccount ? hasPermission('users.edit_own') : hasPermission('users.edit');

  useEffect(() => {
    if (!id) return;
    loadUserDetails();
  }, [id]);

  useEffect(() => {
    if ((activeTab === 'sessions' || activeTab === 'logs') && hasPermission('users.edit')) {
      if (activeTab === 'sessions') {
        loadSessions();
      } else if (activeTab === 'logs') {
        loadAuditLogs();
      }
    }
  }, [activeTab, id, logsPage, logsFilters, sessionsPage, sessionsFilters, sessionsPageSize, logsPageSize]);

  // Recharger les détails de l'utilisateur si la tab validation disparaît (après validation)
  useEffect(() => {
    if (user && user.validation_status !== 'pending_validation' && activeTab === 'validation') {
      // Si l'utilisateur n'est plus en attente de validation mais qu'on est sur la tab validation, recharger
      loadUserDetails();
      setActiveTab('info'); // Passer à la tab info
    }
  }, [user?.validation_status]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      const userResponse = await usersAPI.getById(id);
      setUser(userResponse.data);
      
      // Initialiser userEditData avec les données de l'utilisateur
      setUserEditData({
        username: userResponse.data.username || '',
        email: userResponse.data.email || ''
      });
      
      // Si l'utilisateur est lié à un profil People, charger les détails du membre
      const profileId = userResponse.data.person_id || userResponse.data.member_id;
      if (profileId) {
        try {
          const memberResponse = await peoplePlatformAPI.getById(profileId);
          setMember(mapPeopleToMemberRow(memberResponse.data));
        } catch (err) {
          console.warn('Person profile not found:', err);
          setMember(null);
        }
      } else {
        setMember(null);
      }
    } catch (err) {
      error('Erreur lors du chargement des détails de l\'utilisateur');
      console.error(err);
      navigate(profileShellCompte ? '/profile/compte' : usersListPath);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDate(dateString);
  };

  const loadSessions = async () => {
    try {
      setLoadingActivity(true);
      const params = {
        limit: sessionsPageSize,
        skip: (sessionsPage - 1) * sessionsPageSize
      };
      if (sessionsFilters.startDate) params.start_date = sessionsFilters.startDate;
      if (sessionsFilters.endDate) params.end_date = sessionsFilters.endDate;
      if (sessionsFilters.isActiveOnly) params.is_active_only = true;
      
      const sessionsRes = await usersAPI.getSessions(id, params);
      setSessions(sessionsRes.data.sessions || []);
      setSessionsTotal(sessionsRes.data.total_sessions || 0);
    } catch (err) {
      console.error('Erreur lors du chargement des sessions:', err);
      error('Erreur lors du chargement des sessions');
    } finally {
      setLoadingActivity(false);
    }
  };

  const resetSessionsFilters = () => {
    setSessionsFilters({
      startDate: '',
      endDate: '',
      isActiveOnly: false
    });
    setSessionsPage(1);
  };

  const loadAuditLogs = async () => {
    try {
      setLoadingActivity(true);
      const params = {
        limit: logsPageSize,
        skip: (logsPage - 1) * logsPageSize
      };
      if (logsFilters.action) params.action = logsFilters.action;
      if (logsFilters.resource_type) params.resource_type = logsFilters.resource_type;
      if (logsFilters.startDate) params.start_date = logsFilters.startDate;
      if (logsFilters.endDate) params.end_date = logsFilters.endDate;
      
      const logsRes = await usersAPI.getAuditLogs(id, params);
      setAuditLogs(logsRes.data.logs || []);
      setLogsTotal(logsRes.data.total || 0);
    } catch (err) {
      console.error('Erreur lors du chargement des logs:', err);
      error('Erreur lors du chargement des logs d\'audit');
    } finally {
      setLoadingActivity(false);
    }
  };

  const resetLogsFilters = () => {
    setLogsFilters({
      action: '',
      resource_type: '',
      startDate: '',
      endDate: ''
    });
    setLogsPage(1);
  };

  const sessionsTotalPages = Math.max(1, Math.ceil(sessionsTotal / sessionsPageSize));
  const logsTotalPagesCalculated = Math.max(1, Math.ceil(logsTotal / logsPageSize));

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Connexion',
      'logout': 'Déconnexion',
      'create': 'Création',
      'update': 'Modification',
      'delete': 'Suppression',
      'view': 'Consultation',
      'approve': 'Approbation',
      'reject': 'Rejet'
    };
    return labels[action] || action;
  };

  const getResourceLabel = (resourceType) => {
    const labels = {
      'user': 'Utilisateur',
      'member': 'Membre',
      'contribution': 'Cotisation',
      'transaction': 'Transaction',
      'document': 'Document',
      'event': 'Événement',
      'meeting': 'Procès-verbal',
      'commission': 'Commission'
    };
    return labels[resourceType] || resourceType;
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }
    try {
      await usersAPI.delete(id);
      success('Utilisateur supprimé avec succès');
      navigate(profileShellCompte ? '/profile/compte' : usersListPath);
    } catch (err) {
      error('Erreur lors de la suppression');
      console.error(err);
    }
  };

  const handleUpdateUser = async () => {
    if (!userEditData.username) {
      error('Le nom d\'utilisateur est obligatoire');
      return;
    }
    try {
      setSaving(true);
      const updateData = {
        username: userEditData.username,
        email: userEditData.email || null
      };
      
      await usersAPI.update(id, updateData);
      success('Profil mis à jour avec succès');
      
      setShowEditUserModal(false);
      // Recharger l'utilisateur
      await loadUserDetails();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      setSaving(true);
      const body = { password: passwordData.newPassword };
      if (!isOwnAccount) {
        body.must_change_password = passwordData.forceNextLogin !== false;
      }
      await usersAPI.update(id, body);
      success(
        isOwnAccount
          ? 'Mot de passe modifié avec succès'
          : 'Mot de passe réinitialisé. Communiquez-le à l’utilisateur.'
      );
      setShowPasswordModal(false);
      setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la modification du mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePIN = async () => {
    const cleanNewPin = pinData.newPin.replace(/\D/g, '');
    const cleanConfirmPin = pinData.confirmPin.replace(/\D/g, '');
    const cleanOldPin = pinData.oldPin.replace(/\D/g, '');
    
    if (cleanNewPin.length !== 6 || cleanConfirmPin.length !== 6) {
      error('Le PIN doit contenir exactement 6 chiffres');
      return;
    }
    if (cleanNewPin !== cleanConfirmPin) {
      error('Les PIN ne correspondent pas');
      return;
    }
    if (user?.pin && cleanOldPin.length !== 6) {
      error('L\'ancien PIN est requis');
      return;
    }
    try {
      setSaving(true);
      await authAPI.createPin(cleanNewPin, user?.pin ? cleanOldPin : null);
      success('PIN modifié avec succès');
      setShowPINModal(false);
      setPinData({ oldPin: '', newPin: '', confirmPin: '' });
      // Recharger l'utilisateur
      await loadUserDetails();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la modification du PIN');
    } finally {
      setSaving(false);
    }
  };

  // Charger les informations de validation si l'utilisateur est en attente de validation
  useEffect(() => {
    if (user && user.validation_status === 'pending_validation' && hasPermission('users.edit')) {
      loadValidationInfo();
    }
  }, [user?.validation_status, id]);

  const loadValidationInfo = async () => {
    try {
      setLoadingValidation(true);
      const response = await usersAPI.getValidationInfo(id);
      setValidationInfo(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des informations de validation:', err);
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleSendWhatsApp = () => {
    if (!validationInfo || !validationInfo.whatsapp_message) return;
    
    const phone = validationInfo.phone?.replace(/[^0-9]/g, '') || '';
    const message = encodeURIComponent(validationInfo.whatsapp_message);
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-gray-600">Compte introuvable</p>
          <Link
            to={profileShellCompte ? '/profile/compte' : '/dashboard'}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-entrea-royal px-4 py-2 text-white hover:bg-entrea-navy"
          >
            Retour au profil
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Utilisateur introuvable</p>
          <button
            type="button"
            onClick={() => navigate(profileShellCompte ? '/profile/compte' : usersListPath)}
            className="mt-4 px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy"
          >
            {profileShellCompte ? 'Retour au profil' : 'Retour à la liste'}
          </button>
        </div>
      </div>
    );
  }

  const userIamEditHref = profileShellCompte ? '/profile/compte/modifier' : `${usersBase}/${user.id}/edit`;
  const memberFicheHref =
    member?.id && isOwnAccount ? '/profile' : member?.id ? `${membersBase}/${member.id}` : null;

  const tabs = [
    { id: 'info', name: 'Informations' },
    { id: 'member', name: 'Membre associé' },
    ...(user?.validation_status === 'pending_validation' && hasPermission('users.edit') ? [
      { id: 'validation', name: 'Validation' }
    ] : []),
    ...(hasPermission('users.edit') ? [
      { id: 'sessions', name: 'Sessions' },
      { id: 'logs', name: 'Logs' }
    ] : [])
  ];

  const usernameMatchesPhone =
    Boolean(digitsOnly(user.username)) &&
    digitsOnly(user.username) === digitsOnly(user.phone);

  return (
    <div className={profileShellCompte ? 'min-h-[60vh]' : 'min-h-screen bg-gray-50'}>
      {/* Retour : profil unifié (soi / sous-route /profile/compte) ou liste utilisateurs (admin) */}
      {profileShellCompte ? (
        <div className="mb-3 md:mb-4">
          <Link
            to="/profile"
            className="inline-flex min-h-[40px] items-center gap-1.5 text-sm font-semibold text-[#1f6ab8] hover:text-[#0f3350]"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Mon profil
          </Link>
        </div>
      ) : isOwnAccount ? (
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 shadow-sm">
          <Link
            to="/dashboard"
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm md:text-base font-medium">Tableau de bord</span>
          </Link>
        </div>
      ) : (
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 shadow-sm">
          <button
            type="button"
            onClick={() => navigate(usersListPath)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm md:text-base font-medium">Retour</span>
          </button>
        </div>
      )}

      <div className={`max-w-7xl mx-auto ${profileShellCompte ? 'px-0 py-0' : 'px-3 py-4 md:px-6 md:py-8'}`}>
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div
            className={
              profileShellCompte
                ? 'entrea-portal-trust p-4 md:p-6'
                : 'bg-white rounded-2xl shadow-sm border border-slate-200/90 p-4 md:p-6'
            }
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 md:gap-4">
                <div
                  className={
                    profileShellCompte
                      ? 'flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-white/15 font-display text-2xl font-extrabold text-white ring-1 ring-white/25 md:h-20 md:w-20 md:text-3xl'
                      : 'h-16 w-16 md:h-20 md:w-20 rounded-full bg-gradient-to-br from-entrea-ink to-entrea-royal flex items-center justify-center text-white text-2xl md:text-3xl font-bold flex-shrink-0 ring-2 ring-white shadow-md'
                  }
                >
                  {user.name?.charAt(0) || user.username?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <h1
                    className={
                      profileShellCompte
                        ? 'break-words font-display text-xl font-extrabold tracking-tight text-white sm:text-2xl md:text-3xl'
                        : 'text-xl sm:text-2xl md:text-3xl font-bold text-[#0f3350] break-words'
                    }
                  >
                    {user.name || user.username}
                  </h1>
                  <div
                    className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm md:text-base ${
                      profileShellCompte ? 'text-white/80' : 'text-slate-600'
                    }`}
                  >
                    <span>{formatIamRoleLabel(user.role)}</span>
                    {member?.member_number ? (
                      <>
                        <span className={`${profileShellCompte ? 'text-white/40' : 'text-slate-300'} hidden sm:inline`} aria-hidden>·</span>
                        <span
                          className={
                            profileShellCompte
                              ? 'inline-flex items-center rounded-md bg-white/12 px-2 py-0.5 text-xs font-semibold tracking-wide text-white ring-1 ring-white/20'
                              : 'inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-slate-700'
                          }
                        >
                          N° {member.member_number}
                        </span>
                      </>
                    ) : null}
                  </div>
                  {linkedPersonId && member ? (
                    <p className={`mt-1 text-xs ${profileShellCompte ? 'text-white/65' : 'text-slate-500'}`}>
                      Compte lié au profil People (membre)
                    </p>
                  ) : null}
                  <div className="flex items-center mt-2 gap-2 md:gap-4 flex-wrap">
                    <span
                      className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                        profileShellCompte
                          ? user.is_active
                            ? 'bg-emerald-400/20 text-emerald-100 ring-1 ring-emerald-300/30'
                            : 'bg-red-400/20 text-red-100 ring-1 ring-red-300/30'
                          : user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    {isOrgAdminRole(user.role) && (
                      <span
                        className={
                          profileShellCompte
                            ? 'rounded-full bg-white/12 px-2 py-1 text-xs font-medium text-white ring-1 ring-white/20 md:px-3 md:text-sm'
                            : 'px-2 md:px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs md:text-sm font-medium'
                        }
                      >
                        Administrateur organisation
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-2 sm:gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      setUserEditData({
                        username: user.username || '',
                        email: user.email || ''
                      });
                      setShowEditUserModal(true);
                    }}
                    className={
                      profileShellCompte
                        ? 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-[#113a5b] shadow-sm transition hover:bg-slate-100 md:text-base'
                        : 'px-4 py-2.5 min-h-[44px] bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy flex items-center justify-center gap-2 text-sm md:text-base'
                    }
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="hidden sm:inline">Modifier</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                  {!isOwnAccount && (
                    <button
                      type="button"
                      onClick={() => {
                        setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
                        setShowPasswordModal(true);
                      }}
                      className="px-4 py-2.5 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      <span className="hidden sm:inline">Réinitialiser le mot de passe</span>
                      <span className="sm:hidden">Mot de passe</span>
                    </button>
                  )}
                  {isOwnAccount && (
                    <>
                      <button
                        onClick={() => {
                          setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
                          setShowPasswordModal(true);
                        }}
                        className="px-4 py-2.5 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm md:text-base"
                      >
                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        <span className="hidden sm:inline">Mot de passe</span>
                        <span className="sm:hidden">Pwd</span>
                      </button>
                      <button
                        onClick={() => setShowPINModal(true)}
                        className="px-4 py-2.5 min-h-[44px] bg-entrea-ink text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 text-sm md:text-base"
                      >
                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span className="hidden sm:inline">PIN</span>
                        <span className="sm:hidden">PIN</span>
                      </button>
                    </>
                  )}
                  {hasPermission('users.delete') && (
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2.5 min-h-[44px] bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="hidden sm:inline">Supprimer</span>
                      <span className="sm:hidden">Del</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div
          className={
            profileShellCompte
              ? 'entrea-portal-panel mb-4 md:mb-6 sticky top-0 z-10'
              : 'bg-white rounded-lg shadow-sm border border-gray-200 mb-4 md:mb-6 sticky top-[57px] md:top-0 z-10'
          }
        >
          <div className="border-b border-[#e6edf4]">
            <nav className="flex space-x-1 md:space-x-8 px-3 md:px-6 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-[#2f6fd4] text-[#1f6ab8]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm flex items-center gap-1.5 md:gap-2 flex-shrink-0 min-w-fit`}
                >
                  <TabNavIcon tabId={tab.id} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div
          className={
            profileShellCompte
              ? 'entrea-portal-panel p-4 md:p-6'
              : 'bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6'
          }
        >
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-[#0f3350] mb-3 md:mb-4">Informations de connexion</h3>
                <div className="space-y-3">
                  <DetailRow
                    label="Nom d'utilisateur"
                    hint={usernameMatchesPhone ? 'Identique au téléphone : connexion par numéro (comportement courant).' : undefined}
                  >
                    <span className="font-mono text-[0.95rem]">{user.username || '—'}</span>
                  </DetailRow>
                  {!isOwnAccount && (
                    <DetailRow label="Nom complet">
                      {user.name || '—'}
                    </DetailRow>
                  )}
                  <DetailRow
                    label="Email"
                    hint={!user.email?.trim() && canEdit ? 'Vous pouvez l’ajouter via « Modifier ».' : undefined}
                  >
                    {user.email?.trim() ? (
                      <a href={`mailto:${user.email}`} className="text-entrea-royal hover:underline break-all">
                        {user.email}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic font-normal">Non renseigné</span>
                    )}
                  </DetailRow>
                  <DetailRow
                    label="Téléphone"
                    hint={usernameMatchesPhone ? 'Utilisé aussi comme identifiant de connexion.' : undefined}
                  >
                    {user.phone ? (
                      <span className="font-mono text-[0.95rem]">{user.phone}</span>
                    ) : (
                      <span className="text-slate-400 italic font-normal">—</span>
                    )}
                  </DetailRow>
                </div>
                {isOwnAccount && member?.id && (
                  <div className="mt-6 rounded-xl border border-entrea-panel bg-entrea-panel/30 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Fiche membre</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {member.member_number ? (
                        <span className="rounded-md bg-white px-2 py-1 text-sm font-semibold text-[#0f3350] ring-1 ring-slate-200/80">
                          {member.member_number}
                        </span>
                      ) : null}
                      <Link
                        to={memberFicheHref}
                        className="inline-flex items-center gap-1 text-sm font-medium text-entrea-royal hover:text-entrea-navy"
                      >
                        Ouvrir ma fiche membre
                        <span aria-hidden>→</span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              {!isOwnAccount && (
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-[#0f3350] mb-3 md:mb-4">Informations de compte</h3>
                  <div className="space-y-3">
                    <DetailRow label="Rôle">
                      <span className="inline-flex px-3 py-1 bg-entrea-panel text-entrea-navy rounded-full text-sm font-medium capitalize">
                        {user.role}
                      </span>
                    </DetailRow>
                    <DetailRow label="Statut">
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </DetailRow>
                    <DetailRow label="Date de création">
                      {formatDate(user.created_at)}
                    </DetailRow>
                    <DetailRow label="ID utilisateur (IAM)">
                      <span className="font-mono text-xs break-all text-slate-700">{user.id}</span>
                    </DetailRow>
                    {member?.member_number ? (
                      <DetailRow label="N° membre">
                        <span className="font-semibold tracking-wide">{member.member_number}</span>
                      </DetailRow>
                    ) : null}
                    {linkedPersonId && (
                      <DetailRow label="ID profil People">
                        <span className="font-mono text-xs break-all text-slate-700">{linkedPersonId}</span>
                        {member?.id ? (
                          <div className="mt-2">
                            <Link
                              to={`${membersBase}/${member.id}`}
                              className="text-sm font-medium text-entrea-royal hover:text-entrea-navy"
                            >
                              Voir la fiche membre →
                            </Link>
                          </div>
                        ) : null}
                      </DetailRow>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'validation' && user?.validation_status === 'pending_validation' && hasPermission('users.edit') && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Validation du compte</h2>
                <p className="text-gray-600">Ce compte est en attente de validation. Envoyez le lien de validation et le PIN au membre via WhatsApp.</p>
              </div>

              {loadingValidation ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement...</p>
                </div>
              ) : validationInfo?.has_validation ? (
                <div className="space-y-4">
                  {/* Lien de validation */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-900 mb-2">Lien de validation</h3>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={validationInfo.validation_url}
                        className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm font-mono text-gray-900"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(validationInfo.validation_url);
                          success('Lien copié dans le presse-papiers');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        Copier
                      </button>
                    </div>
                  </div>

                  {/* PIN par défaut */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-medium text-yellow-900 mb-2">PIN par défaut</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 bg-white border border-yellow-300 rounded-lg text-2xl font-bold text-center text-gray-900 font-mono tracking-wider">
                        {validationInfo.default_pin}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(validationInfo.default_pin);
                          success('PIN copié dans le presse-papiers');
                        }}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
                      >
                        Copier
                      </button>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">Message à envoyer</h3>
                    <div className="bg-white border border-gray-300 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                      {validationInfo.whatsapp_message}
                    </div>
                  </div>

                  {/* Bouton WhatsApp */}
                  <div className="flex justify-center">
                    <button
                      onClick={handleSendWhatsApp}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-3 text-base font-medium shadow-lg"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Envoyer via WhatsApp
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>Ce compte n'est pas en attente de validation.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sessions' && hasPermission('users.edit') && (
            <div>
              {/* Filtres */}
              <div className="mb-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date début</label>
                    <input
                      type="date"
                      value={sessionsFilters.startDate}
                      onChange={(e) => {
                        setSessionsFilters({ ...sessionsFilters, startDate: e.target.value });
                        setSessionsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date fin</label>
                    <input
                      type="date"
                      value={sessionsFilters.endDate}
                      onChange={(e) => {
                        setSessionsFilters({ ...sessionsFilters, endDate: e.target.value });
                        setSessionsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                    <label className="flex items-center mt-2">
                      <TableListCheckbox
                        checked={sessionsFilters.isActiveOnly}
                        onChange={(e) => {
                          setSessionsFilters({ ...sessionsFilters, isActiveOnly: e.target.checked });
                          setSessionsPage(1);
                        }}
                        aria-label="Afficher uniquement les sessions actives"
                      />
                      <span className="ml-2 text-sm text-gray-700">Sessions actives uniquement</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={resetSessionsFilters}
                  className="text-sm text-entrea-royal hover:text-entrea-navy font-medium"
                >
                  Réinitialiser les filtres
                </button>
              </div>

              {loadingActivity ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement des sessions...</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 text-sm text-gray-600">
                    {sessionsTotal === 0 ? <span>Aucune session trouvée</span> : null}
                  </div>
                  {sessions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">Aucune session enregistrée</p>
                  ) : (
                    <>
                      {sessionsTotal > 0 && (
                        <Pagination
                          placement="top"
                          currentPage={sessionsPage}
                          totalPages={sessionsTotalPages}
                          totalItems={sessionsTotal}
                          itemsPerPage={sessionsPageSize}
                          onPageChange={setSessionsPage}
                          onPageSizeChange={(n) => {
                            setSessionsPageSize(n);
                            setSessionsPage(1);
                          }}
                        />
                      )}
                      <div className="space-y-3 mb-4">
                        {sessions.map((session, index) => (
                          <div
                            key={index}
                            className={`border rounded-lg p-4 ${
                              session.is_active
                                ? 'border-green-200 bg-green-50'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  {session.is_active && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                      Session active
                                    </span>
                                  )}
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatDateTime(session.login_time)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({getTimeAgo(session.login_time)})
                                  </span>
                                </div>
                                {session.last_activity && (
                                  <p className="text-xs text-gray-600 mb-2">
                                    Dernière activité : {formatDateTime(session.last_activity)} ({getTimeAgo(session.last_activity)})
                                  </p>
                                )}
                                {session.ip_address && (
                                  <p className="text-xs text-gray-500">
                                    IP: {session.ip_address}
                                  </p>
                                )}
                                {session.user_agent && (
                                  <p className="text-xs text-gray-500 truncate" title={session.user_agent}>
                                    {session.user_agent.substring(0, 80)}...
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {sessionsTotal > 0 && (
                        <Pagination
                          placement="bottom"
                          currentPage={sessionsPage}
                          totalPages={sessionsTotalPages}
                          totalItems={sessionsTotal}
                          itemsPerPage={sessionsPageSize}
                          onPageChange={setSessionsPage}
                          onPageSizeChange={(n) => {
                            setSessionsPageSize(n);
                            setSessionsPage(1);
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'logs' && hasPermission('users.edit') && (
            <div>
              {/* Filtres */}
              <div className="mb-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
                    <select
                      value={logsFilters.action}
                      onChange={(e) => {
                        setLogsFilters({ ...logsFilters, action: e.target.value });
                        setLogsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    >
                      <option value="">Toutes les actions</option>
                      <option value="login">Connexion</option>
                      <option value="logout">Déconnexion</option>
                      <option value="create">Création</option>
                      <option value="update">Modification</option>
                      <option value="delete">Suppression</option>
                      <option value="view">Consultation</option>
                      <option value="approve">Approbation</option>
                      <option value="reject">Rejet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type de ressource</label>
                    <select
                      value={logsFilters.resource_type}
                      onChange={(e) => {
                        setLogsFilters({ ...logsFilters, resource_type: e.target.value });
                        setLogsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    >
                      <option value="">Tous les types</option>
                      <option value="user">Utilisateur</option>
                      <option value="member">Membre</option>
                      <option value="contribution">Cotisation</option>
                      <option value="transaction">Transaction</option>
                      <option value="document">Document</option>
                      <option value="event">Événement</option>
                      <option value="meeting">Procès-verbal</option>
                      <option value="commission">Commission</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date début</label>
                    <input
                      type="date"
                      value={logsFilters.startDate}
                      onChange={(e) => {
                        setLogsFilters({ ...logsFilters, startDate: e.target.value });
                        setLogsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date fin</label>
                    <input
                      type="date"
                      value={logsFilters.endDate}
                      onChange={(e) => {
                        setLogsFilters({ ...logsFilters, endDate: e.target.value });
                        setLogsPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40"
                    />
                  </div>
                </div>
                <button
                  onClick={resetLogsFilters}
                  className="text-sm text-entrea-royal hover:text-entrea-navy font-medium"
                >
                  Réinitialiser les filtres
                </button>
              </div>

              {loadingActivity ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement des logs...</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 text-sm text-gray-600">
                    {logsTotal === 0 ? <span>Aucun log trouvé</span> : null}
                  </div>
                  {auditLogs.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">Aucun log d'audit</p>
                  ) : (
                    <>
                      {logsTotal > 0 && (
                        <Pagination
                          placement="top"
                          currentPage={logsPage}
                          totalPages={logsTotalPagesCalculated}
                          totalItems={logsTotal}
                          itemsPerPage={logsPageSize}
                          onPageChange={setLogsPage}
                          onPageSizeChange={(n) => {
                            setLogsPageSize(n);
                            setLogsPage(1);
                          }}
                        />
                      )}
                      <div className="space-y-2 mb-4">
                        {auditLogs.map((log) => (
                          <div
                            key={log.id}
                            className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  log.action === 'login' ? 'bg-blue-100 text-blue-800' :
                                  log.action === 'logout' ? 'bg-gray-100 text-gray-800' :
                                  log.action === 'create' ? 'bg-green-100 text-green-800' :
                                  log.action === 'update' ? 'bg-yellow-100 text-yellow-800' :
                                  log.action === 'delete' ? 'bg-red-100 text-red-800' :
                                  'bg-entrea-panel text-entrea-navy'
                                }`}>
                                  {getActionLabel(log.action)}
                                </span>
                                <span className="px-2 py-1 bg-entrea-panel text-purple-800 text-xs font-medium rounded-full">
                                  {getResourceLabel(log.resource_type)}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {getTimeAgo(log.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-1">
                              {formatDateTime(log.created_at)}
                            </p>
                            {log.resource_id && (
                              <p className="text-xs text-gray-500 mb-1">
                                Ressource ID: <span className="font-mono">{log.resource_id}</span>
                              </p>
                            )}
                            {log.ip_address && (
                              <p className="text-xs text-gray-500">
                                IP: {log.ip_address}
                              </p>
                            )}
                            {log.details && Object.keys(log.details).length > 0 && (
                              <details className="mt-2">
                                <summary className="text-xs text-entrea-royal cursor-pointer hover:text-entrea-navy">
                                  Voir les détails
                                </summary>
                                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                      {logsTotal > 0 && (
                        <Pagination
                          placement="bottom"
                          currentPage={logsPage}
                          totalPages={logsTotalPagesCalculated}
                          totalItems={logsTotal}
                          itemsPerPage={logsPageSize}
                          onPageChange={setLogsPage}
                          onPageSizeChange={(n) => {
                            setLogsPageSize(n);
                            setLogsPage(1);
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'member' && (
            <div>
              {member ? (
                <div className="space-y-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-slate-600 to-entrea-royal flex items-center justify-center text-white text-xl font-bold flex-shrink-0 ring-2 ring-white shadow">
                        {member.name?.charAt(0) || '?'}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg md:text-xl font-semibold text-[#0f3350] break-words">{member.name}</h3>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {member.member_number ? (
                            <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-sm font-bold tracking-wide text-slate-800">
                              {member.member_number}
                            </span>
                          ) : null}
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            member.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {member.status === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                          <span className="text-xs uppercase tracking-wide text-slate-500">
                            {member.type === 'member' ? 'Membre' : member.type || 'Profil'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={memberFicheHref}
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-entrea-royal px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-entrea-navy transition-colors min-h-[44px]"
                    >
                      Ouvrir la fiche membre
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <DetailRow label="Email">
                      {member.email?.trim() ? (
                        <a href={`mailto:${member.email}`} className="text-entrea-royal hover:underline break-all">
                          {member.email}
                        </a>
                      ) : (
                        <span className="text-slate-400 italic font-normal">Non renseigné</span>
                      )}
                    </DetailRow>
                    <DetailRow label="Téléphone">
                      {member.phone ? <span className="font-mono text-[0.95rem]">{member.phone}</span> : '—'}
                    </DetailRow>
                    <DetailRow label="Commission / groupe">
                      {member.commission || (member.groups?.length ? member.groups.join(', ') : 'Aucune')}
                    </DetailRow>
                    <DetailRow label="ID profil People">
                      <span className="font-mono text-xs break-all text-slate-700">{member.id}</span>
                    </DetailRow>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  {linkedPersonId ? (
                    <div>
                      <p>Le membre associé n'a pas été trouvé</p>
                      <p className="text-sm mt-2">ID: {linkedPersonId}</p>
                    </div>
                  ) : (
                    <div>
                      <p>Aucun membre associé à cet utilisateur</p>
                      <button
                        onClick={() => navigate(userIamEditHref)}
                        className="mt-4 px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy"
                      >
                        Modifier l'utilisateur
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal pour changer le mot de passe */}
      {showPasswordModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowPasswordModal(false);
            setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
          }}
        >
          <div className="relative p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {isOwnAccount ? 'Changer le mot de passe' : 'Réinitialiser le mot de passe'}
              </h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {!isOwnAccount ? (
                <p className="text-sm text-gray-600">
                  Définissez un mot de passe provisoire pour{' '}
                  <span className="font-medium text-gray-900">{user.username || user.email || 'cet utilisateur'}</span>.
                </p>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  minLength={6}
                />
              </div>
              {passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                <p className="text-sm text-red-600">Les mots de passe ne correspondent pas</p>
              )}
              {!isOwnAccount ? (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={passwordData.forceNextLogin !== false}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, forceNextLogin: e.target.checked })
                    }
                    className="mt-1 rounded border-gray-300 text-entrea-royal focus:ring-entrea-royal/40"
                  />
                  <span className="text-sm text-gray-700">
                    Exiger un changement de mot de passe à la prochaine connexion
                  </span>
                </label>
              ) : null}
              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleChangePassword}
                  disabled={saving || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : isOwnAccount ? 'Changer le mot de passe' : 'Réinitialiser'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ newPassword: '', confirmPassword: '', forceNextLogin: true });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Modal pour changer le PIN */}
      {showPINModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowPINModal(false);
            setPinData({ oldPin: '', newPin: '', confirmPin: '' });
          }}
        >
          <div className="relative p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Changer le PIN</h3>
              <button
                onClick={() => {
                  setShowPINModal(false);
                  setPinData({ oldPin: '', newPin: '', confirmPin: '' });
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {user?.pin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Ancien PIN</label>
                  <PINInput
                    value={pinData.oldPin}
                    onChange={(value) => setPinData({ ...pinData, oldPin: value })}
                    onComplete={(value) => setPinData({ ...pinData, oldPin: value })}
                    disabled={saving}
                    autoFocus={false}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Nouveau PIN</label>
                <PINInput
                  value={pinData.newPin}
                  onChange={(value) => setPinData({ ...pinData, newPin: value })}
                  onComplete={(value) => setPinData({ ...pinData, newPin: value })}
                  disabled={saving}
                  autoFocus={!user?.pin}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Confirmer le nouveau PIN</label>
                <PINInput
                  value={pinData.confirmPin}
                  onChange={(value) => setPinData({ ...pinData, confirmPin: value })}
                  onComplete={(value) => setPinData({ ...pinData, confirmPin: value })}
                  disabled={saving}
                  autoFocus={false}
                />
              </div>
              {pinData.newPin && pinData.confirmPin && pinData.newPin.replace(/\D/g, '') !== pinData.confirmPin.replace(/\D/g, '') && (
                <p className="text-sm text-red-600 text-center">Les PIN ne correspondent pas</p>
              )}
              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleChangePIN}
                  disabled={saving || pinData.newPin.replace(/\D/g, '').length !== 6 || pinData.confirmPin.replace(/\D/g, '').length !== 6 || (user?.pin && pinData.oldPin.replace(/\D/g, '').length !== 6)}
                  className="flex-1 px-4 py-2 bg-entrea-ink text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Changer le PIN'}
                </button>
                <button
                  onClick={() => {
                    setShowPINModal(false);
                    setPinData({ oldPin: '', newPin: '', confirmPin: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Modal pour modifier le profil utilisateur */}
      {showEditUserModal && (
        <ModalShell
          maxWidthClass="max-w-md"
          onBackdropClick={() => {
            setShowEditUserModal(false);
            setUserEditData({
              username: user?.username || '',
              email: user?.email || '',
            });
          }}
        >
          <div className="relative p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Modifier le profil</h3>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setUserEditData({
                    username: user?.username || '',
                    email: user?.email || ''
                  });
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur *</label>
                <input
                  type="text"
                  value={userEditData.username}
                  onChange={(e) => setUserEditData({ ...userEditData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  placeholder="Nom d'utilisateur"
                />
                <p className="text-xs text-gray-500 mt-1">Le nom d'utilisateur correspond généralement à votre numéro de téléphone</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={userEditData.email || ''}
                  onChange={(e) => setUserEditData({ ...userEditData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="exemple@email.com"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note :</strong> Pour modifier votre numéro de téléphone, veuillez contacter un administrateur. Cette modification nécessite une approbation.
                </p>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleUpdateUser}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button
                  onClick={() => {
                    setShowEditUserModal(false);
                    setUserEditData({
                      username: user?.username || '',
                      email: user?.email || ''
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default UserDetails;

